import { useIntl, type IntlShape } from "react-intl";
import type { AgentProgress, FilterDescription } from "@/lib/agent-agui";

/**
 * What the turn has done so far, in the pill and the position the bouncing dots used to occupy.
 *
 * The last [PROGRESS_DEPTH] steps, oldest first, the newest carrying the animated dots. Earlier
 * steps dim rather than disappear, so the path reads as a sequence instead of a flicker.
 * `aria-live` because the dots announced nothing at all.
 */
/**
 * The flattened `where` in the viewer's language.
 *
 * Every operator is a message id, so nothing English leaks into a German line. An operator this
 * build does not know falls back to its schema name — the same degrade-don't-disappear rule the
 * phases follow.
 */
function describeFilter(filter: FilterDescription, intl: IntlShape): string {
  const joiner = intl.formatMessage({
    id: filter.logic === "or" ? "agent.progress.logic.or" : "agent.progress.logic.and",
    defaultMessage: filter.logic === "or" ? "or" : "and",
  });
  return filter.conditions
    .map((c) => {
      const op = OPERATOR_MESSAGES[c.op];
      const word = op ? intl.formatMessage(op) : c.op;
      const field = fieldLabel(c.field, intl);
      if (c.values.length > 0) return `${field} ${word} [${c.values.join(", ")}]`;
      if (c.value === undefined) return `${field} ${word}`;
      return `${field} ${word} '${c.value}'`;
    })
    .join(` ${joiner} `);
}

/**
 * A schema field path in the viewer's words: `industry.name` reads as "Branche", not as a path.
 * Reference fields are keyed by their outer segment (`country.id` and `country.name` are both the
 * country), so the map holds paths AND outer segments; an unknown path stays as it is, which is still
 * readable and still true.
 */
function fieldLabel(path: string, intl: IntlShape): string {
  const outer = path.split(".")[0] ?? path;
  const message = FIELD_MESSAGES[path] ?? FIELD_MESSAGES[outer];
  return message ? intl.formatMessage(message) : path;
}

/** Field path (or its outer segment) → wording. Keyed by what agent-service sends in `FilterCondition.field`. */
const FIELD_MESSAGES: Record<string, { id: string; defaultMessage: string }> = {
  name: { id: "agent.progress.field.name", defaultMessage: "name" },
  "name.family": { id: "agent.progress.field.name.family", defaultMessage: "surname" },
  "name.given": { id: "agent.progress.field.name.given", defaultMessage: "first name" },
  formattedName: { id: "agent.progress.field.formattedName", defaultMessage: "name" },
  id: { id: "agent.progress.field.id", defaultMessage: "id" },
  jobTitle: { id: "agent.progress.field.jobTitle", defaultMessage: "job title" },
  profession: { id: "agent.progress.field.profession", defaultMessage: "profession" },
  currentEmployer: { id: "agent.progress.field.currentEmployer", defaultMessage: "employer" },
  city: { id: "agent.progress.field.city", defaultMessage: "city" },
  country: { id: "agent.progress.field.country", defaultMessage: "country" },
  countryState: { id: "agent.progress.field.countryState", defaultMessage: "state" },
  nationality: { id: "agent.progress.field.nationality", defaultMessage: "nationality" },
  citizenships: { id: "agent.progress.field.citizenships", defaultMessage: "citizenship" },
  gender: { id: "agent.progress.field.gender", defaultMessage: "gender" },
  ageInYears: { id: "agent.progress.field.ageInYears", defaultMessage: "age" },
  birthDate: { id: "agent.progress.field.birthDate", defaultMessage: "date of birth" },
  languageSkills: { id: "agent.progress.field.languageSkills", defaultMessage: "languages" },
  expertises: { id: "agent.progress.field.expertises", defaultMessage: "expertise" },
  experiences: { id: "agent.progress.field.experiences", defaultMessage: "experience" },
  educations: { id: "agent.progress.field.educations", defaultMessage: "education" },
  industry: { id: "agent.progress.field.industry", defaultMessage: "industry" },
  employeeCount: { id: "agent.progress.field.employeeCount", defaultMessage: "number of employees" },
  summary: { id: "agent.progress.field.summary", defaultMessage: "description" },
  competencyFieldName: { id: "agent.progress.field.competencyFieldName", defaultMessage: "expertise" },
};

/** Operator name → its wording. Keyed by the schema operator agent-service sends. */
const OPERATOR_MESSAGES: Record<string, { id: string; defaultMessage: string }> = {
  eq: { id: "agent.progress.op.eq", defaultMessage: "is exactly" },
  ne: { id: "agent.progress.op.ne", defaultMessage: "is not" },
  contains: { id: "agent.progress.op.contains", defaultMessage: "like" },
  startsWith: { id: "agent.progress.op.startsWith", defaultMessage: "starts with" },
  endsWith: { id: "agent.progress.op.endsWith", defaultMessage: "ends with" },
  in: { id: "agent.progress.op.in", defaultMessage: "in" },
  notIn: { id: "agent.progress.op.notIn", defaultMessage: "not in" },
  lt: { id: "agent.progress.op.lt", defaultMessage: "is less than" },
  lte: { id: "agent.progress.op.lte", defaultMessage: "is at most" },
  gt: { id: "agent.progress.op.gt", defaultMessage: "is greater than" },
  gte: { id: "agent.progress.op.gte", defaultMessage: "is at least" },
  any: { id: "agent.progress.op.any", defaultMessage: "is set" },
};

export function ProgressText({ progress }: { progress: AgentProgress | null }) {
  const intl = useIntl();

  // No stage yet: spark owns the default, so a fast turn still says something rather than showing
  // wordless dots.
  if (!progress) {
    return (
      <>{intl.formatMessage({ id: "agent.progress.considering", defaultMessage: "Considering" })}</>
    );
  }

  // Preferred: the flattened conditions, worded here so a German line reads as German. The
  // rendered fragment agent-service sends alongside is the fallback for a `where` it could not
  // flatten. An empty filter means the call was unfiltered, and every sentence that reads well with
  // a fragment reads badly with a word standing in for one ("Counting people where everyone"), so
  // the unfiltered case gets its own sentence rather than a placeholder value.
  const filter = progress.filter ? describeFilter(progress.filter, intl) : (progress.params.filter ?? "");

  // Which kind of thing the call is about. agent-service's company query sends `entity: companies`;
  // the person query sends nothing, and nothing is people, as it always was.
  const companies = progress.params.entity === "companies";

  switch (progress.phase) {
    case "counting":
      return (
        <>
          {filter
            ? companies
              ? intl.formatMessage(
                  { id: "agent.progress.counting.companies", defaultMessage: "Counting companies where {filter}" },
                  { filter },
                )
              : intl.formatMessage(
                  { id: "agent.progress.counting", defaultMessage: "Counting people where {filter}" },
                  { filter },
                )
            : companies
              ? intl.formatMessage({ id: "agent.progress.countingAll.companies", defaultMessage: "Counting all companies" })
              : intl.formatMessage({
                  id: "agent.progress.countingAll",
                  defaultMessage: "Counting everyone",
                })}
        </>
      );
    case "listing":
      return (
        <>
          {filter
            ? companies
              ? intl.formatMessage(
                  { id: "agent.progress.listing.companies", defaultMessage: "Looking up companies where {filter}" },
                  { filter },
                )
              : intl.formatMessage(
                  { id: "agent.progress.listing", defaultMessage: "Looking up people where {filter}" },
                  { filter },
                )
            : companies
              ? intl.formatMessage({ id: "agent.progress.listingAll.companies", defaultMessage: "Looking up companies" })
              : intl.formatMessage({
                  id: "agent.progress.listingAll",
                  defaultMessage: "Looking up people",
                })}
        </>
      );
    case "tallying": {
      const field = fieldLabel(progress.params.field ?? "", intl);
      return (
        <>
          {filter
            ? intl.formatMessage(
                { id: "agent.progress.tallying", defaultMessage: "Tallying {field} where {filter}" },
                { field, filter },
              )
            : intl.formatMessage(
                {
                  id: "agent.progress.tallyingAll",
                  defaultMessage: "Tallying {field} across everyone",
                },
                { field },
              )}
        </>
      );
    }
    case "resolving_condition":
      return (
        <>
          {intl.formatMessage(
            {
              id: "agent.progress.resolvingCondition",
              defaultMessage: "Condition {index} of {total}: {condition}",
            },
            {
              index: progress.params.index ?? "?",
              total: progress.params.total ?? "?",
              condition: progress.params.condition ?? "",
            },
          )}
        </>
      );
    case "condition_reworded":
      return (
        <>
          {intl.formatMessage(
            {
              id: "agent.progress.conditionReworded",
              defaultMessage: "Stored as ''{value}'' instead",
            },
            { value: progress.params.value ?? "" },
          )}
        </>
      );
    case "condition_unrecorded":
      return (
        <>
          {intl.formatMessage(
            {
              id: "agent.progress.conditionUnrecorded",
              defaultMessage: "Field ''{field}'' is barely filled in, using ''{insteadField}'' instead",
            },
            {
              field: progress.params.field ?? "",
              insteadField: progress.params.insteadField ?? "",
            },
          )}
        </>
      );
    case "condition_absent":
      return (
        <>
          {intl.formatMessage(
            {
              id: "agent.progress.conditionAbsent",
              defaultMessage: "Nothing in the data matches ''{field}''",
            },
            { field: progress.params.field ?? "" },
          )}
        </>
      );
    case "combining":
      return (
        <>
          {intl.formatMessage(
            {
              id: "agent.progress.combining",
              defaultMessage: "Putting it together: {filter}",
            },
            { filter: progress.params.filter ?? "" },
          )}
        </>
      );
    case "searching_alternatives":
      return (
        <>
          {intl.formatMessage({
            id: "agent.progress.searchingAlternatives",
            defaultMessage: "Did not find any, starting a deeper search",
          })}
        </>
      );
    case "alternative_found":
      return (
        <>
          {intl.formatMessage(
            {
              id: "agent.progress.alternativeFound",
              defaultMessage: 'Found it under "{term}"',
            },
            { term: progress.params.term ?? "" },
          )}
        </>
      );
    // The company search's FIRST line: the question is being taken apart, and then the same line says
    // which half will answer it. Worth a line of its own because it is a second or two of silence
    // before anything else happens, and because the answer to it is the difference between a quick
    // reply and a long one — a reader who sees "reading the descriptions" knows to wait.
    case "planning_search": {
      // Shaped like its siblings (`searching_web`, `locating_place`): the value, then one return.
      const route = progress.params.route ?? "";
      if (!route) {
        return (
          <>
            {intl.formatMessage({
              id: "agent.progress.planningSearch",
              defaultMessage: "Working out where to look",
            })}
          </>
        );
      }
      // The `other` arm SHOWS the value rather than falling back to the no-route wording. Rendering
      // the identical string made an unrecognised route — a renamed arm, or an uppercase enum name —
      // indistinguishable from "not decided yet": the pill would read "Working out where to look" for
      // the whole search, through the long summaries pause, with nothing in the console and no test
      // failing. Same convention as the unknown operator and unknown field path above: fall back to
      // something still readable and still true, carrying the value.
      return (
        <>
          {intl.formatMessage(
            {
              // THE RENAMED ROUTES KEEP THEIR OLD ARMS. A turn's progress trail is kept server-side
              // (`agent_subgraph_steps`) and replayed when the thread is reopened, so a thread
              // answered before 2026-10-15 still carries `rag`, `field`, `both` or `record` — and
              // without an arm each one fell to `other` and was shown to the reader as
              // "Working out where to look (rag)".
              id: "agent.progress.planningSearchRoute",
              defaultMessage:
                "{route, select, label {Looking in the expertise labels} description {Reading the company summaries} description_and_label {Looking in the expertise labels and the summaries} named {Reading the named companies} field {Looking in the expertise labels} rag {Reading the company summaries} both {Looking in the expertise labels and the summaries} record {Reading the named companies} other {Working out where to look ({route})}}",
            },
            { route },
          )}
        </>
      );
    }
    // The summary route (@external-adr AGENT-SERVICE-0032-company-subgraph-description-trigger-and-rag):
    // index, search, count. No id list
    // ever reaches the pill: the declaration names ids, and ids mean nothing to a reader.
    // The field half of a company search: the expertise labels, the industry and the name, asked
    // before the summaries are read.
    case "searching_fields":
      // NAMED WHEN THERE IS ONE TO NAME. There is more than one company graph now and a reader
      // picks between them, so a line that says only "the labels" cannot be told from the same
      // question asked of another graph — and the two give different answers. No `graph` param
      // means the graph route was not taken at all, and then the labels really are what was read.
      return (
        <>
          {progress.params.graph
            ? intl.formatMessage(
                {
                  id: "agent.progress.searchingGraph",
                  defaultMessage: 'Searching the company graph "{graph}" for "{terms}"',
                },
                { graph: progress.params.graph, terms: progress.params.terms ?? "" },
              )
            : intl.formatMessage(
                { id: "agent.progress.searchingFields", defaultMessage: 'Checking the expertise labels for "{terms}"' },
                { terms: progress.params.terms ?? "" },
              )}
        </>
      );
    // The label vocabulary being filled (ADR AGENT-SERVICE-0044). Counted, unlike its summary
    // sibling: one genesis call per company, so a cold run is minutes long and a static line would
    // read as a hang.
    case "embedding_labels":
      return (
        <>
          {intl.formatMessage(
            {
              id: "agent.progress.embeddingLabels",
              defaultMessage: "Reading the expertise labels, company {company} of {companies}",
            },
            { company: progress.params.company ?? "", companies: progress.params.companies ?? "" },
          )}
        </>
      );
    case "indexing_summaries":
      return (
        <>
          {intl.formatMessage({
            id: "agent.progress.indexingSummaries",
            defaultMessage: "Creating an index of the company summaries",
          })}
        </>
      );
    case "searching_summaries":
      return (
        <>
          {intl.formatMessage(
            { id: "agent.progress.searchingSummaries", defaultMessage: 'Searching the summaries for "{query}"' },
            { query: progress.params.query ?? "" },
          )}
        </>
      );
    // The long pause at the end of a company search: the hits are back and the model is reading every
    // one of them to write a verdict. One line for the whole batch, because it is one model call.
    case "weighing_hits": {
      // Where the number comes from. Four lines saying "10 candidates" followed by "the 18
      // candidates" is an arithmetic riddle: 40 hits were returned, 22 of them repeats. The server
      // sends all three numbers; `returned`/`searches` are absent on events from before it did, and
      // then the short form is the honest one.
      const returned = Number(progress.params.returned ?? "");
      const searches = Number(progress.params.searches ?? "");
      const explained = Number.isFinite(returned) && Number.isFinite(searches) && returned > 0 && searches > 0;
      return (
        <>
          {explained
            ? intl.formatMessage(
                {
                  id: "agent.progress.weighingHitsExplained",
                  defaultMessage:
                    "Reading the {hits} candidates and weighing them up – {returned} hits from {searches} searches, {hits} unique in total",
                },
                {
                  hits: progress.params.hits ?? "",
                  returned: progress.params.returned ?? "",
                  searches: progress.params.searches ?? "",
                },
              )
            : intl.formatMessage(
                {
                  id: "agent.progress.weighingHits",
                  defaultMessage: "Reading the {hits} candidates and weighing them up",
                },
                { hits: progress.params.hits ?? "" },
              )}
        </>
      );
    }
    case "locating_place": {
      const place = progress.params.place ?? "";
      const area = progress.params.in ?? "";
      return (
        <>
          {area
            ? intl.formatMessage(
                { id: "agent.progress.locatingPlaceIn", defaultMessage: "Checking whether {place} is in {area}" },
                { place, area },
              )
            : intl.formatMessage(
                { id: "agent.progress.locatingPlace", defaultMessage: "Checking where {place} is" },
                { place },
              )}
        </>
      );
    }
    case "summaries_matched": {
      const count = Number(progress.params.count ?? "0");
      // A malformed count is NaN, and NaN > 0 is false: without the guard it would read as "no company
      // describes itself that way", which is a claim about the data, not about the message.
      if (!Number.isFinite(count)) {
        return <>{intl.formatMessage({ id: "agent.progress.working", defaultMessage: "Working on it" })}</>;
      }
      return (
        <>
          {count > 0
            ? intl.formatMessage(
                { id: "agent.progress.summariesMatched", defaultMessage: "{count, plural, one {# company} other {# companies}} found by summary" },
                { count },
              )
            : intl.formatMessage({
                id: "agent.progress.summariesMatchedNone",
                defaultMessage: "No company describes itself that way",
              })}
        </>
      );
    }
    case "nothing_found":
      return (
        <>
          {intl.formatMessage({
            id: "agent.progress.nothingFound",
            defaultMessage: "Confirmed there is nothing like that",
          })}
        </>
      );
    // One public-web search (@external-adr AGENT-SERVICE-0034-web-search-as-a-separate-tool): the
    // question travels cut to a line; without it the sentence stands alone rather than quoting nothing.
    case "searching_web": {
      const query = progress.params.query ?? "";
      return (
        <>
          {query
            ? intl.formatMessage(
                { id: "agent.progress.searchingWeb", defaultMessage: 'Searching the web for "{query}"' },
                { query },
              )
            : intl.formatMessage({ id: "agent.progress.searchingWebAll", defaultMessage: "Searching the web" })}
        </>
      );
    }
    // One PubMed search, a research source the admin offers: the query is the term that went to PubMed (often
    // English or MeSH, not the question), so the reader sees what was actually searched and where.
    case "searching_pubmed": {
      const query = progress.params.query ?? "";
      return (
        <>
          {query
            ? intl.formatMessage(
                { id: "agent.progress.searchingPubmed", defaultMessage: 'Searching PubMed for "{query}"' },
                { query },
              )
            : intl.formatMessage({ id: "agent.progress.searchingPubmedAll", defaultMessage: "Searching PubMed" })}
        </>
      );
    }
    default:
      console.debug("[agent] unknown progress phase", progress.phase);
      return (
        <>
          {intl.formatMessage({ id: "agent.progress.working", defaultMessage: "Working on it" })}
        </>
      );
  }
}
